import { useEffect, useState, useRef } from 'react';
import { getParticipantesPool, getOrigenesPool, uploadParticipantesPoolExcel, getEventosAdmin, addInscritosDesdePool } from '../../../services/eventosService';
import AdminSpinner from '../components/AdminSpinner';
import { formatNombre, formatRut, formatPeso, formatPesoCategoriaLista, formatCategoriaEdad } from '../../../utils/formatoParticipantes';

const styles = {
  container: { padding: '28px 24px', maxWidth: '1200px', margin: '0 auto' },
  header: { marginBottom: '28px' },
  title: { margin: 0, fontSize: '26px', fontWeight: 700, letterSpacing: '-0.02em', color: '#1a1a1a' },
  subtitle: { color: '#555', marginTop: '8px', fontSize: '14px', lineHeight: 1.5 },
  card: {
    background: '#fff',
    borderRadius: '12px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
    padding: '24px',
    marginTop: '20px',
    border: '1px solid #eee'
  },
  sectionTitle: { margin: '0 0 12px', fontSize: '13px', fontWeight: 600, color: '#444', textTransform: 'uppercase', letterSpacing: '0.04em' },
  row: { display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'center' },
  input: { border: '1px solid #ddd', borderRadius: '8px', fontSize: '14px' },
  btnPrimary: { padding: '10px 18px', background: '#1a4d8c', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 600, fontSize: '14px' },
  btnGhost: { padding: '10px 18px', background: '#f8f9fa', color: '#333', border: '1px solid #dee2e6', borderRadius: '8px', cursor: 'pointer', fontWeight: 600, fontSize: '14px' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: '14px' },
  th: { textAlign: 'left', padding: '12px 10px', borderBottom: '2px solid #e8e8e8', color: '#555', fontWeight: 600, fontSize: '13px' },
  td: { padding: '12px 10px', borderBottom: '1px solid #f0f0f0' },
  empty: { textAlign: 'center', padding: '32px 24px', color: '#666', fontSize: '14px' },
  tableWrap: { overflowX: 'auto', maxHeight: '60vh', overflowY: 'auto', marginTop: '16px' },
  exampleBox: { marginTop: '20px', padding: '16px', backgroundColor: '#f8f9fa', borderRadius: '10px', border: '1px solid #e9ecef' },
  filterLabel: { fontSize: '12px', fontWeight: 600, color: '#555', marginBottom: '6px', display: 'block' }
};

const ParticipantesView = () => {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [toast, setToast] = useState(null);
  const [origenFilter, setOrigenFilter] = useState('');
  const [tituloCarga, setTituloCarga] = useState('');
  const [origenes, setOrigenes] = useState([]);
  const [eventos, setEventos] = useState([]);
  const [eventoPorOrigen, setEventoPorOrigen] = useState({});
  const [inscribiendoOrigen, setInscribiendoOrigen] = useState(null);
  const fileInputRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const filtros = {};
      if (origenFilter) filtros.origen = origenFilter;
      const data = await getParticipantesPool(filtros);
      setList(Array.isArray(data) ? data : []);
    } catch {
      setList([]);
    } finally {
      setLoading(false);
    }
  };

  /** Agrupa participantes por nombre del archivo (origen_carga). */
  const listByOrigen = () => {
    const map = {};
    for (const p of list) {
      const key = p.origen_carga && p.origen_carga.trim() ? p.origen_carga.trim() : 'Sin nombre de archivo';
      if (!map[key]) map[key] = [];
      map[key].push(p);
    }
    return Object.keys(map)
      .sort((a, b) => a.localeCompare(b))
      .map((nombre) => ({ nombre, participantes: map[nombre] }));
  };

  const loadEventos = async () => {
    try {
      const data = await getEventosAdmin();
      setEventos(Array.isArray(data) ? data : []);
    } catch {
      setEventos([]);
    }
  };

  useEffect(() => {
    load();
  }, [origenFilter]);

  useEffect(() => {
    loadEventos();
  }, []);

  useEffect(() => {
    getOrigenesPool().then(setOrigenes).catch(() => setOrigenes([]));
  }, []);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 3500);
    return () => clearTimeout(t);
  }, [toast]);

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const titleToSend = tituloCarga.trim() || (file.name || '').replace(/\.xlsx?$/i, '').trim() || undefined;
    if (!tituloCarga.trim()) setTituloCarga((file.name || '').replace(/\.xlsx?$/i, '').trim());
    setUploading(true);
    try {
      const data = await uploadParticipantesPoolExcel(file, titleToSend);
      setToast({ type: 'success', message: data.mensaje || 'Lista actualizada.' });
      await load();
      getOrigenesPool().then(setOrigenes).catch(() => {});
    } catch (err) {
      setToast({ type: 'error', message: err.message || 'Error al subir Excel' });
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleInscribirGrupo = async (origenNombre, participantes) => {
    const eventoId = eventoPorOrigen[origenNombre];
    if (!eventoId || participantes.length === 0) {
      setToast({ type: 'error', message: 'Elige un evento para este grupo.' });
      return;
    }
    setInscribiendoOrigen(origenNombre);
    try {
      const ruts = participantes.map((p) => p.rut);
      const data = await addInscritosDesdePool(Number(eventoId), ruts);
      const msg = data.mensaje || `Se agregaron ${data.agregados ?? 0} participante(s).`;
      if (data.ya_inscritos?.length) {
        setToast({ type: 'success', message: `${msg} (${data.ya_inscritos.length} ya estaban inscritos.)` });
      } else {
        setToast({ type: 'success', message: msg });
      }
    } catch (err) {
      setToast({ type: 'error', message: err.message || 'Error al inscribir' });
    } finally {
      setInscribiendoOrigen(null);
    }
  };

  return (
    <div style={styles.container}>
      <header style={styles.header}>
        <h1 style={styles.title}>Lista de participantes</h1>
        <p style={styles.subtitle}>
          Sube Excels (cada uno con un nombre, ej. CRONOGRAMA NACIONAL). La lista se agrupa por archivo; elige evento y &quot;Inscribir todos en el evento&quot; por grupo.
        </p>
        <p style={{ ...styles.subtitle, marginTop: '6px', fontStyle: 'italic', color: '#888', fontSize: '13px' }}>
          Próximamente: inscripción desde la página pública del evento.
        </p>
      </header>

      <section style={styles.card}>
        <p style={styles.sectionTitle}>Cargar lista maestra</p>
        <div style={{ marginBottom: '16px' }}>
          <span style={styles.filterLabel}>Nombre del archivo / título</span>
          <p style={{ margin: '2px 0 8px', fontSize: '12px', color: '#666' }}>
            Si no lo escribes, se usará el nombre del archivo (ej. CRONOGRAMA NACIONAL.xlsx → CRONOGRAMA NACIONAL).
          </p>
          <div style={styles.row}>
            <input
              type="text"
              value={tituloCarga}
              onChange={(e) => setTituloCarga(e.target.value)}
              placeholder="Nacional 2025, Zonal Centro, …"
              style={{ ...styles.input, padding: '8px 12px', width: '280px' }}
            />
            <input
              type="file"
              ref={fileInputRef}
              accept=".xlsx,.xls"
              style={{ display: 'none' }}
              onChange={handleFileChange}
            />
            <button
              type="button"
              style={styles.btnPrimary}
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
            >
              {uploading ? 'Subiendo…' : 'Subir Excel a la lista'}
            </button>
            <button type="button" style={styles.btnGhost} onClick={load} disabled={loading}>
              Actualizar lista
            </button>
          </div>
        </div>
        <div style={{ marginBottom: '16px' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: '#555' }}>
            <span style={styles.filterLabel}>Ver solo:</span>
            <select
              value={origenFilter}
              onChange={(e) => setOrigenFilter(e.target.value)}
              style={{ ...styles.input, padding: '6px 10px', minWidth: '200px' }}
            >
              <option value="">Todos los archivos</option>
              {origenes.map((o) => (
                <option key={o} value={o}>{o}</option>
              ))}
            </select>
          </label>
        </div>

        <div style={styles.exampleBox}>
          <p style={{ margin: 0, marginBottom: '10px', fontSize: '13px', fontWeight: 600, color: '#333' }}>Plantilla de ejemplo</p>
          <a
            href="/ejemplo/Ejemplo_Plantilla.xlsx"
            download="Ejemplo_Plantilla.xlsx"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              padding: '10px 16px',
              backgroundColor: '#1a4d8c',
              color: '#fff',
              borderRadius: '8px',
              fontWeight: 600,
              fontSize: '14px',
              textDecoration: 'none',
            }}
          >
            Descargar plantilla Excel (Ejemplo_Plantilla.xlsx)
          </a>
        </div>

        {loading ? (
          <div style={{ marginTop: '24px' }}>
            <AdminSpinner label="Cargando participantes…" />
          </div>
        ) : list.length === 0 ? (
          <p style={styles.empty}>
            No hay participantes en la lista. Descarga la plantilla de ejemplo, complétala y súbela aquí.
          </p>
        ) : (
          <>
            <p style={styles.sectionTitle}>
              Lista por archivo · {list.length} participante{list.length !== 1 ? 's' : ''} en total
            </p>
            {listByOrigen().map(({ nombre, participantes }) => (
              <div
                key={nombre}
                style={{
                  marginTop: '24px',
                  padding: '16px',
                  border: '1px solid #e8e8e8',
                  borderRadius: '10px',
                  backgroundColor: '#fafafa'
                }}
              >
                <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                  <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 700, color: '#1a1a1a', textTransform: 'uppercase' }}>
                    {nombre}
                  </h3>
                  <span style={{ fontSize: '13px', color: '#666' }}>
                    {participantes.length} participante{participantes.length !== 1 ? 's' : ''}
                  </span>
                  <div style={{ marginLeft: 'auto', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px' }}>
                    <select
                      value={eventoPorOrigen[nombre] || ''}
                      onChange={(e) => setEventoPorOrigen((prev) => ({ ...prev, [nombre]: e.target.value }))}
                      style={{ ...styles.input, padding: '6px 10px', minWidth: '220px' }}
                    >
                      <option value="">— Seleccionar evento —</option>
                      {eventos.map((e) => (
                        <option key={e.id} value={e.id}>{e.nombre || `Evento ${e.id}`}</option>
                      ))}
                    </select>
                    <button
                      type="button"
                      style={styles.btnPrimary}
                      onClick={() => handleInscribirGrupo(nombre, participantes)}
                      disabled={inscribiendoOrigen !== null || !(eventoPorOrigen[nombre])}
                    >
                      {inscribiendoOrigen === nombre ? 'Inscribiendo…' : `Inscribir todos en el evento`}
                    </button>
                  </div>
                </div>
                <div style={styles.tableWrap}>
                  <table style={styles.table}>
                    <thead>
                      <tr>
                        <th style={styles.th}>Nombre</th>
                        <th style={styles.th}>RUT</th>
                        <th style={styles.th}>División</th>
                        <th style={styles.th}>Categoría peso TESTED</th>
                        <th style={styles.th}>Categoría peso UNTESTED</th>
                        <th style={styles.th}>Categoría edad</th>
                        <th style={styles.th}>Modalidad</th>
                        <th style={styles.th}>Día</th>
                      </tr>
                    </thead>
                    <tbody>
                      {participantes.map((p) => (
                        <tr key={p.rut}>
                          <td style={styles.td}>{formatNombre(p.nombre)}</td>
                          <td style={styles.td}>{formatRut(p.rut)}</td>
                          <td style={styles.td}>{p.division || '—'}</td>
                          <td style={styles.td}>{formatPesoCategoriaLista(p.categoria_peso_tested, p.division)}</td>
                          <td style={styles.td}>{formatPesoCategoriaLista(p.categoria_peso_untested, p.division)}</td>
                          <td style={styles.td}>{formatCategoriaEdad(p.categoria_edad)}</td>
                          <td style={styles.td}>{p.modalidad || '—'}</td>
                          <td style={styles.td}>{p.dia_salida || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
          </>
        )}
      </section>

      {toast && (
        <div
          style={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            background: toast.type === 'error' ? '#c62828' : '#2e7d32',
            color: '#fff',
            padding: '14px 18px',
            borderRadius: 12,
            boxShadow: '0 6px 18px rgba(0,0,0,0.25)',
            fontWeight: 700,
            fontSize: 14,
            zIndex: 9999
          }}
        >
          {toast.message}
        </div>
      )}
    </div>
  );
};

export default ParticipantesView;
